import type { FC, MouseEvent, ReactElement } from 'react';

import classNames from 'classnames';

import { getUserLabel } from '@iconik-io/core/src/get_user_label';
import type { AssetsSegmentSchemaWithIdPatched } from '@iconik-io/iconik-api/src/schema_patched/assets_segments';
import type { SegmentEntityWithIdNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/segment';
import { createEntityDenormalizer } from '@iconik-io/shared-lib/src/redux/entities/normalizers';
import {
  selectAssetVersionExtended,
  selectAssetVersionsExtended,
} from '@iconik-io/shared-lib/src/redux/entities/selectors/versions';

import { useAppSelector } from '../../store/react_redux_typed';
import IconButton from '../../ui/icon_button/icon_button';
import AsyncUser from '../async_user/async_user';
import TooltipTrigger from '../tooltip_trigger/tooltip_trigger';
import { SegmentHeaderUser } from './segment_header_user';

import styles from './segment.module.css';

type Props = {
  segment: SegmentEntityWithIdNormalized;
  canEdit: boolean;
  checked?: boolean;
  onCheck?: (segmentId: string, name: string, value: boolean) => void;
  onEdit: (event: MouseEvent) => void;
  onApproveClick?: (event: MouseEvent) => void;
  editButtonDisabled?: boolean;
  editButtonPrependElement?: ReactElement;
};

const getSegmentUserName = (segment: AssetsSegmentSchemaWithIdPatched) => {
  if (segment.user_info) {
    return getUserLabel(segment.user_info);
  }
  if (segment.share_user_email) {
    return segment.share_user_email;
  }
  if (segment.external_id) {
    return segment.external_id.split(':')[3];
  }
  return undefined;
};

const denormalizeSegment = createEntityDenormalizer('segments');

export const SegmentHeader: FC<Props> = ({
  segment,
  canEdit,
  onEdit,
  checked,
  onApproveClick,
  onCheck,
  editButtonDisabled,
  editButtonPrependElement,
}) => {
  const denormalizedSegment = useAppSelector((state) =>
    denormalizeSegment(state.entities, segment.id),
  );
  const userName = denormalizedSegment
    ? getSegmentUserName(denormalizedSegment)
    : undefined;

  const version = useAppSelector((state) =>
    segment.version_id
      ? selectAssetVersionExtended(state, segment.asset_id, segment.version_id)
      : undefined,
  );
  const versions = useAppSelector((state) =>
    selectAssetVersionsExtended(state, segment.asset_id),
  );

  return (
    <div className={styles.top}>
      <div className={styles.data}>
        {!userName && segment.user_id ? (
          <AsyncUser id={segment.user_id} nowrap>
            {({ user }) => (
              <SegmentHeaderUser
                photoUrl={user.photo}
                userName={getUserLabel(user) || 'Unknown user'}
                dateCreated={segment.date_created}
                versionName={
                  version && versions.length > 1 ? version.name : undefined
                }
              />
            )}
          </AsyncUser>
        ) : undefined}
        {userName ? (
          <SegmentHeaderUser
            photoUrl={segment.user_photo}
            userName={userName}
            dateCreated={segment.date_created}
            versionName={
              version && versions.length > 1 ? version.name : undefined
            }
          />
        ) : undefined}
        {canEdit ? (
          <>
            {editButtonPrependElement}
            <div className={styles.buttons_right}>
              <IconButton
                aria-label="Edit comment"
                onClick={onEdit}
                iconName="edit"
                disabled={editButtonDisabled}
              />
            </div>
          </>
        ) : undefined}
        {onCheck && onApproveClick ? (
          <div className={styles.actions}>
            <TooltipTrigger
              tooltipText={!checked && 'Mark Complete'}
              placement="left"
              disabled={checked}
              componentProps={{
                className: styles.check_button_container,
              }}
            >
              <IconButton
                aria-label="Mark comment as complete"
                iconName={
                  checked ? 'loader_circle_done_legacy' : 'annotation_circle'
                }
                onClick={onApproveClick}
                className={classNames(styles.check_button, {
                  [styles.checked]: checked,
                })}
                uiKey="approve-comment-button"
              />
            </TooltipTrigger>
          </div>
        ) : null}
      </div>
    </div>
  );
};
